import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";

import ModalImage from "react-modal-image";
import "./modal-image-override.css";
import api from "../../services/api";

const useStyles = makeStyles(theme => ({
	messageMedia: {
		objectFit: "cover",
		width: 250,
		height: "auto",
		borderTopLeftRadius: 8,
		borderTopRightRadius: 8,
		borderBottomLeftRadius: 8,
		borderBottomRightRadius: 8,
	},
}));

const ModalImageCors = ({ imageUrl }) => {
	const classes = useStyles();
	const [fetching, setFetching] = useState(true);
	const [blobUrl, setBlobUrl] = useState("");
	const zoomScaleRef = useRef(1);
	const blobUrlRef = useRef(""); // ✅ Ref para rastrear blob URL atual

	useEffect(() => {
		if (!imageUrl) return;
		
		const fetchImage = async () => {
			try {
				let normalizedUrl = imageUrl;
				let shouldFetchAsBlob = false;
				
				// ✅ CORREÇÃO: Se a URL já é completa (http/https), usar diretamente SEM fazer requisição blob
				// Isso evita erros ERR_FAILED desnecessários quando a imagem já está acessível
				if (normalizedUrl.startsWith('http://') || normalizedUrl.startsWith('https://')) {
					// ✅ CORREÇÃO: URLs completas devem ser usadas diretamente pelo navegador
					// A requisição blob só é necessária em casos específicos de CORS ou processamento
					// Como a imagem já está sendo exibida corretamente, não precisamos fazer requisição adicional
					setBlobUrl(normalizedUrl);
					setFetching(false);
					return;
				} else {
					// URL relativa, precisa fazer requisição blob para garantir autenticação
					if (!normalizedUrl.startsWith('/')) {
						normalizedUrl = `/${normalizedUrl}`;
					}
					shouldFetchAsBlob = true;
				}
				
				// ✅ CORREÇÃO: Só fazer requisição blob se necessário
				if (shouldFetchAsBlob) {
					try {
						// ✅ Adicionar timeout e melhor tratamento de erro
						const controller = new AbortController();
						const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 segundos
						
						try {
							const { data, headers } = await api.get(normalizedUrl, {
								responseType: "blob",
								signal: controller.signal,
								timeout: 10000,
							});
							
							clearTimeout(timeoutId);
							
							// ✅ Verificar se o blob é válido
							if (!data || data.size === 0) {
								throw new Error('Blob vazio recebido');
							}
							
							const contentType = headers["content-type"] || "image/jpeg";
							const url = window.URL.createObjectURL(
								new Blob([data], { type: contentType })
							);
							
							// ✅ Revogar blob URL anterior antes de definir nova
							if (blobUrlRef.current && blobUrlRef.current.startsWith('blob:')) {
								window.URL.revokeObjectURL(blobUrlRef.current);
							}
							
							blobUrlRef.current = url;
							setBlobUrl(url);
							setFetching(false);
						} catch (fetchError) {
							clearTimeout(timeoutId);
							
							// ✅ CORREÇÃO: Suprimir erro silenciosamente quando for net::ERR_FAILED
							// A imagem pode estar sendo exibida corretamente mesmo com erro na requisição blob
							const errorMessage = String(fetchError?.message || '');
							const errorCode = String(fetchError?.code || '');
							
							// Não logar se for erro de rede comum (ERR_FAILED, Network Error, etc)
							const shouldSuppressError = 
								errorMessage.includes('ERR_FAILED') || 
								errorMessage.includes('Network Error') ||
								errorCode.includes('ERR_FAILED') ||
								fetchError?.response?.status === 404;
							
							if (!shouldSuppressError && 
							    fetchError.name !== 'AbortError' && 
							    fetchError.code !== 'ECONNABORTED' &&
							    fetchError?.response?.status !== 401 && 
							    fetchError?.response?.status !== 403) {
								console.warn("[ModalImageCors] Erro ao carregar imagem como blob, usando URL original:", fetchError);
							}
							
							// ✅ Usar URL original como fallback (imagem será carregada diretamente)
							setBlobUrl(imageUrl);
							setFetching(false);
						}
					} catch (error) {
						// ✅ CORREÇÃO: Em caso de qualquer erro, usar URL original silenciosamente
						setBlobUrl(imageUrl);
						setFetching(false);
					}
				} else {
					// Não precisa fazer requisição, usar URL diretamente
					setBlobUrl(imageUrl);
					setFetching(false);
				}
				
			} catch (error) {
				// ✅ CORREÇÃO: Em caso de qualquer erro, usar URL original silenciosamente
				setBlobUrl(imageUrl);
				setFetching(false);
			}
		};
		
		fetchImage();
		
		// ✅ Cleanup: revogar blob URL quando componente desmontar ou URL mudar
		return () => {
			if (blobUrlRef.current && blobUrlRef.current.startsWith('blob:')) {
				window.URL.revokeObjectURL(blobUrlRef.current);
				blobUrlRef.current = "";
			}
		};
	}, [imageUrl]);

	// Adicionar controles customizados de Zoom + e Zoom -
	useEffect(() => {
		const addZoomControls = () => {
			const modalContainer = document.querySelector('.__react_modal_image__modal_container');
			const iconMenu = document.querySelector('.__react_modal_image__icon_menu');
			const image = document.querySelector('#react-modal-image-img');
			
			if (!modalContainer || !iconMenu || !image) return;

			// ✅ CORREÇÃO: Prevenir propagação apenas para duplo clique nos botões do modal
			// Não interceptar cliques simples, apenas duplos cliques para evitar marcar mensagem como resposta
			// Deixar os botões nativos do react-modal-image funcionarem normalmente
			const preventDoubleClickPropagation = () => {
				const iconMenuLinks = iconMenu.querySelectorAll('a');
				iconMenuLinks.forEach(link => {
					// Remover listeners antigos se existirem
					const existingDblClickHandler = link._preventPropagationDblClickHandler;
					
					if (existingDblClickHandler) {
						link.removeEventListener('dblclick', existingDblClickHandler, false);
					}
					
					// Apenas prevenir duplo clique de propagar (para evitar marcar mensagem como resposta)
					// NÃO interceptar clique simples, deixar react-modal-image processar normalmente
					const dblClickHandler = (e) => {
						// Prevenir duplo clique de propagar para a mensagem
						e.stopPropagation();
					};
					
					// Armazenar referência para poder remover depois
					link._preventPropagationDblClickHandler = dblClickHandler;
					
					// Adicionar listener apenas para duplo clique na fase de bubbling
					link.addEventListener('dblclick', dblClickHandler, false);
				});
			};
			
			// Executar imediatamente
			preventDoubleClickPropagation();
			
			// Observar mudanças no menu para interceptar novos botões
			const menuObserver = new MutationObserver(() => {
				preventDoubleClickPropagation();
			});
			menuObserver.observe(iconMenu, {
				childList: true,
				subtree: true
			});

			// Verificar se os controles já foram adicionados
			if (document.getElementById('zoom-plus-control') || document.getElementById('zoom-minus-control')) {
				return;
			}

			// Função para aplicar zoom
			const applyZoom = (delta) => {
				zoomScaleRef.current = Math.max(0.5, Math.min(5, zoomScaleRef.current + delta));
				const currentTransform = image.style.transform || '';
				const rotationMatch = currentTransform.match(/rotate\(([^)]+)\)/);
				const rotation = rotationMatch ? rotationMatch[1] : '0deg';
				
				// Aplicar zoom mantendo rotação e posição
				image.style.transform = `translate3d(-50%, -50%, 0) scale(${zoomScaleRef.current}) rotate(${rotation})`;
				image.style.WebkitTransform = `translate3d(-50%, -50%, 0) scale(${zoomScaleRef.current}) rotate(${rotation})`;
				image.style.MsTransform = `translate3d(-50%, -50%, 0) scale(${zoomScaleRef.current}) rotate(${rotation})`;
			};

			// Criar botão Zoom +
			const zoomPlusBtn = document.createElement('a');
			zoomPlusBtn.id = 'zoom-plus-control';
			zoomPlusBtn.href = '#';
			zoomPlusBtn.innerHTML = `
				<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<circle cx="12" cy="12" r="10" stroke="white" stroke-width="2.5" fill="none"></circle>
					<line x1="12" y1="7" x2="12" y2="17" stroke="white" stroke-width="3" stroke-linecap="round"></line>
					<line x1="7" y1="12" x2="17" y2="12" stroke="white" stroke-width="3" stroke-linecap="round"></line>
				</svg>
			`;
			zoomPlusBtn.onclick = (e) => {
				e.preventDefault();
				e.stopPropagation();
				applyZoom(0.25);
			};
			zoomPlusBtn.style.cssText = `
				display: flex !important;
				align-items: center !important;
				justify-content: center !important;
				opacity: 1 !important;
				visibility: visible !important;
				background-color: rgba(0, 0, 0, 0.7) !important;
				padding: 12px !important;
				border-radius: 50% !important;
				margin: 0 !important;
				color: white !important;
				cursor: pointer !important;
				text-decoration: none !important;
				pointer-events: auto !important;
				width: 48px !important;
				height: 48px !important;
				box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3) !important;
				transition: all 0.2s ease !important;
			`;
			const plusSvg = zoomPlusBtn.querySelector('svg');
			if (plusSvg) {
				plusSvg.style.cssText = `
					display: block !important;
					width: 32px !important;
					height: 32px !important;
					opacity: 1 !important;
					visibility: visible !important;
				`;
			}

			// Criar botão Zoom -
			const zoomMinusBtn = document.createElement('a');
			zoomMinusBtn.id = 'zoom-minus-control';
			zoomMinusBtn.href = '#';
			zoomMinusBtn.innerHTML = `
				<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<circle cx="12" cy="12" r="10" stroke="white" stroke-width="2.5" fill="none"></circle>
					<line x1="7" y1="12" x2="17" y2="12" stroke="white" stroke-width="3" stroke-linecap="round"></line>
				</svg>
			`;
			zoomMinusBtn.onclick = (e) => {
				e.preventDefault();
				e.stopPropagation();
				applyZoom(-0.25);
			};
			zoomMinusBtn.style.cssText = zoomPlusBtn.style.cssText;
			const minusSvg = zoomMinusBtn.querySelector('svg');
			if (minusSvg) {
				minusSvg.style.cssText = `
					display: block !important;
					width: 32px !important;
					height: 32px !important;
					opacity: 1 !important;
					visibility: visible !important;
				`;
			}

			// Adicionar hover effects
			zoomPlusBtn.onmouseenter = () => {
				zoomPlusBtn.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
				zoomPlusBtn.style.transform = 'scale(1.1)';
			};
			zoomPlusBtn.onmouseleave = () => {
				zoomPlusBtn.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
				zoomPlusBtn.style.transform = 'scale(1)';
			};
			zoomMinusBtn.onmouseenter = () => {
				zoomMinusBtn.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
				zoomMinusBtn.style.transform = 'scale(1.1)';
			};
			zoomMinusBtn.onmouseleave = () => {
				zoomMinusBtn.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
				zoomMinusBtn.style.transform = 'scale(1)';
			};

			// Inserir antes do botão de fechar (último botão)
			const closeBtn = iconMenu.querySelector('a:last-child');
			if (closeBtn) {
				iconMenu.insertBefore(zoomPlusBtn, closeBtn);
				iconMenu.insertBefore(zoomMinusBtn, closeBtn);
			} else {
				iconMenu.appendChild(zoomPlusBtn);
				iconMenu.appendChild(zoomMinusBtn);
			}

			// Resetar zoom quando o modal fechar
			const observer = new MutationObserver(() => {
				if (!document.querySelector('.__react_modal_image__modal_container')) {
					zoomScaleRef.current = 1;
				}
			});
			observer.observe(document.body, { childList: true, subtree: true });
		};

		// Observar quando o modal aparecer
		const observer = new MutationObserver(() => {
			setTimeout(addZoomControls, 100);
		});
		observer.observe(document.body, {
			childList: true,
			subtree: true
		});

		// Tentar adicionar imediatamente
		setTimeout(addZoomControls, 100);
		
		// ✅ Adicionar listener global para prevenir propagação de duplo clique quando o modal estiver aberto
		// Isso previne que duplos cliques nos botões marquem a mensagem como resposta
		const handleGlobalDblClick = (e) => {
			const modalContainer = document.querySelector('.__react_modal_image__modal_container');
			if (modalContainer && modalContainer.contains(e.target)) {
				const iconMenu = document.querySelector('.__react_modal_image__icon_menu');
				// Se o duplo clique foi em um botão do menu, prevenir propagação
				if (iconMenu && iconMenu.contains(e.target)) {
					e.stopPropagation();
				}
			}
		};
		
		// Usar capture phase apenas para duplo clique, pois o clique simples já é tratado pelos botões
		document.addEventListener('dblclick', handleGlobalDblClick, true);

		return () => {
			observer.disconnect();
			document.removeEventListener('dblclick', handleGlobalDblClick, true);
		};
	}, []);

	return (
		<>
			<ModalImage
				className={classes.messageMedia}
				small={fetching ? imageUrl : blobUrl}
				medium={fetching ? imageUrl : blobUrl}
				large={fetching ? imageUrl : blobUrl}
				alt="image"
				showRotate={true}
				hideDownload={false}
				hideZoom={false}
			/>
		</>
	);
};

export default ModalImageCors;
